import { navigate } from 'gatsby';
import parse, { domToReact, Element } from 'html-react-parser';
import parseCss from 'style-to-object';

import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';

import * as s from '../styles/article.module.scss';

import type { MouseEventHandler } from 'react';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';

const headingTags = [
  'h2',
  'h3',
];

const handleAnchorClick = (id: string) => {
  navigator.clipboard.writeText(`${location.origin}${location.pathname}#${id}`);

  if (!window.location.href.includes(id)) {
    navigate(`#${id}`, { replace: true });
  }
};

const readingTime = (text = '') => {
  const wpm = 250;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wpm);
};

const getTextContent = (domNode: Element) => {
  let innerText = '';

  if (domNode?.children?.length > 0) {
    domNode?.children?.forEach((child) => {
      if (child?.type === 'text') {
        innerText += child?.data;
      }
      if (child?.type === 'tag') {
        innerText += getTextContent(child);
      }
    });
  }

  return innerText;
};

const parser = (htmlBody: string, imgCallback: MouseEventHandler<HTMLImageElement> | undefined ) => {
  const h2List: { id: string; name: string; }[] = [];
  let index = 0;

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && (domNode.attribs?.class === 'accordion__item')) {
        domNode.tagName = 'details';
      }
      if (domNode instanceof Element && (domNode.attribs?.class === 'accordion__item-title')) {
        domNode.tagName = 'summary';
      }
      if (domNode instanceof Element && (domNode.tagName === 'img')) {
        const { class: className, style, ...rest } = domNode?.attribs || {};
        let parsedStyle = undefined;
        if (style) {
          parsedStyle = parseCss(style);
        }
        return <img {...rest} className={className} style={parsedStyle ?? undefined} onClick={imgCallback} />;
      }
      
      if (domNode instanceof Element && (headingTags.includes(domNode.tagName))) {
        const text = getTextContent(domNode);
        const id = domNode.attribs.id || `${text.toLowerCase().replace(/[^\p{Letter}\p{Mark}]/gu, '-').replace(/^[^\p{Letter}\p{Mark}]+/gu, '')}${index}`;
        const HeadingTag = `${domNode.tagName}` as keyof JSX.IntrinsicElements;
        if (domNode.tagName === 'h2') {
          h2List.push({
            id,
            name: text,
          });
        }
        index += 1;
        return (
          <>
            <a className='visuallyHidden' id={`hdg-${index}`}>{text}</a>
            <HeadingTag id={id}>
            {domNode instanceof Element ? domToReact((domNode as Element)?.children as DOMNode[]) : undefined}
              <div className={s.linkContainer} data-id={id} onClick={() => handleAnchorClick(id)}>
                <Icon className={s.anchorLink} fillColor='currentColor' iconName='AnchorLink' />
              </div>
            </HeadingTag>
          </>
        );
      }
      return null;
    },
  };

  return {
    h2List,
    parsedBody: parse(htmlBody, parserOptions),
    readTime: readingTime(htmlBody),
  };
};

export default parser;
